import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader, Dices, LayoutGrid, List, X } from "lucide-react";

import "../styles/owlNest.css";
import { API_BASE } from "../config";
import apiFetch from "../apiFetch";

const rarityOrder  = ["Common", "Uncommon", "Rare", "Epic", "Legendary", "Mythical"];
const rarityColors = {
  Common:    "#6b7280",
  Uncommon:  "#10b981",
  Rare:      "#3b82f6",
  Epic:      "#8b5cf6",
  Legendary: "#f59e0b",
  Mythical:  "#ec4899",
};

function formatTime(s) {
  if (s <= 0) return "Ready!";
  const h   = Math.floor(s / 3600);
  const m   = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

const OwlNestPage = () => {
  const [userOwls,           setUserOwls]           = useState([]);
  const [allOwls,            setAllOwls]            = useState([]);
  const [username,           setUsername]           = useState("");
  const [loading,            setLoading]            = useState(true);
  const [error,              setError]              = useState(null);
  const [showFullCollection, setShowFullCollection] = useState(false);
  const [selectedOwl,        setSelectedOwl]        = useState(null);
  const [highNest,           setHighNest]           = useState(Array(10).fill(null));
  const [rarityStats,        setRarityStats]        = useState({});
  const [nestUserId,         setNestUserId]         = useState(null);
  const [timers,             setTimers]             = useState({});
  const [popupOwl,           setPopupOwl]           = useState(null);
  const [collectingOwlId,    setCollectingOwlId]    = useState(null);
  const [activateSource,     setActivateSource]     = useState(null);
  const [coinToast,          setCoinToast]          = useState(null);
  const [coins,              setCoins]              = useState(null);
  const navigate = useNavigate();
  const popupRef = useRef(null);

  // ── Mount ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const storedId       = localStorage.getItem("userId");
    const storedUsername = localStorage.getItem("displayName") || localStorage.getItem("username");
    if (!storedId || !storedUsername) {
      setError("User not logged in");
      setLoading(false);
      return;
    }
    setUsername(storedUsername);
    const uid = parseInt(storedId, 10);
    setNestUserId(uid);
    fetchUserCollection(storedId);
    fetchAllOwls(uid);
    fetchCoins(storedId);
  }, []);

  // ── Rarity stats ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (allOwls.length > 0) calculateRarityStats(allOwls);
  }, [userOwls, allOwls]);

  // ── 1-second client countdown ──────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      setTimers((prev) => {
        let changed = false;
        const next = {};
        for (const [owlId, t] of Object.entries(prev)) {
          if (t.secondsLeft > 0) {
            const newLeft = t.secondsLeft - 1;
            next[owlId] = { ...t, secondsLeft: newLeft, isReady: newLeft === 0 };
            changed = true;
          } else {
            next[owlId] = t;
          }
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // ── 30-second server sync ──────────────────────────────────────────────────
  useEffect(() => {
    if (!nestUserId) return;
    const id = setInterval(() => fetchTimers(nestUserId), 30000);
    return () => clearInterval(id);
  }, [nestUserId]);

  // ── Popup Escape key + focus ───────────────────────────────────────────────
  useEffect(() => {
    if (!popupOwl) return;
    popupRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") setPopupOwl(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [popupOwl]);

  // ── Activate-mode Escape ───────────────────────────────────────────────────
  useEffect(() => {
    if (!activateSource) return;
    const onKey = (e) => { if (e.key === "Escape") setActivateSource(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [activateSource]);

  // ── Coin toast auto-dismiss ────────────────────────────────────────────────
  useEffect(() => {
    if (!coinToast) return;
    const id = setTimeout(() => setCoinToast(null), 2500);
    return () => clearTimeout(id);
  }, [coinToast]);

  // ── Data fetch helpers ─────────────────────────────────────────────────────
  const fetchCoins = async (userId) => {
    try {
      const res = await apiFetch(`/api/resources/${userId}`);
      if (!res.ok) return;
      const data = await res.json();
      if (typeof data.coins === "number") setCoins(data.coins);
    } catch { /* silent */ }
  };

  const fetchUserCollection = async (userId) => {
    try {
      const res = await apiFetch(`/api/collection?userId=${userId}`);
      if (!res.ok) throw new Error();
      const owls = await res.json();
      setUserOwls(owls);
      if (Array.isArray(owls) && owls.length)
        localStorage.setItem("owlCache", JSON.stringify(owls.map(o => ({ name: o.name, image_url: o.image_url }))));
    } catch { setError("Failed to load collection"); }
  };

  const fetchAllOwls = async (userId, attempt = 0) => {
    try {
      const [owlsRes, nestRes] = await Promise.all([
        apiFetch(`/api/owls`),
        apiFetch(`/api/high-nest?userId=${userId}`),
      ]);
      if (!owlsRes.ok) throw new Error();
      const data = await owlsRes.json();
      setAllOwls(data);
      calculateRarityStats(data);
      if (nestRes.ok) {
        const nestSlots = await nestRes.json();
        const restored = Array(10).fill(null);
        nestSlots.forEach(({ slot_index, ...owl }) => {
          if (slot_index >= 0 && slot_index < 10) restored[slot_index] = owl;
        });
        setHighNest(restored);
      }
      setLoading(false);
      fetchTimers(userId);
    } catch {
      if (attempt === 0) {
        setTimeout(() => fetchAllOwls(userId, 1), 1500);
      } else {
        setError("Failed to load owl database");
        setLoading(false);
      }
    }
  };

  const fetchTimers = async (userId) => {
    try {
      const res = await apiFetch(`/api/nest/timers?userId=${userId}`);
      if (!res.ok) return;
      const data = await res.json();
      const map = {};
      data.forEach(({ owlId, secondsLeft, isReady, mechanic }) => {
        map[owlId] = { secondsLeft, isReady, mechanic };
      });
      setTimers(map);
    } catch { /* silent */ }
  };

  const calculateRarityStats = (owlsData) => {
    const seenIds = new Set();
    const collectedByRarity = {};
    userOwls.forEach((o) => {
      if (!seenIds.has(o.id)) {
        seenIds.add(o.id);
        collectedByRarity[o.rarity] = (collectedByRarity[o.rarity] || 0) + 1;
      }
    });
    const stats = {};
    rarityOrder.forEach((rarity) => {
      stats[rarity] = {
        collected: collectedByRarity[rarity] || 0,
        total:     owlsData.filter((o) => o.rarity === rarity).length,
      };
    });
    setRarityStats(stats);
  };

  const getOwlImage    = (owl) => owl?.image_url ? `${API_BASE}${owl.image_url}` : `${process.env.PUBLIC_URL}/images/owlTestImgNoBG.png`;
  const isOwlCollected = (id)  => userOwls.some((o) => o.id === id);
  const isInHighNest   = (id)  => highNest.some((o) => o?.id === id);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleOwlClick = (owl) => {
    if (!isOwlCollected(owl.id)) return;
    setPopupOwl(owl);
  };

  const handleHighNestSlotClick = async (slotIndex) => {
    if (!selectedOwl) return;
    if (highNest.some((o) => o?.id === selectedOwl.id)) return;
    const prev = [...highNest];
    const next = [...highNest];
    next[slotIndex] = selectedOwl;
    setHighNest(next);
    setSelectedOwl(null);
    try {
      const res = await apiFetch(`/api/high-nest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: nestUserId, owlId: selectedOwl.id, slotIndex }),
      });
      if (!res.ok) throw new Error();
      fetchTimers(nestUserId);
    } catch { setHighNest(prev); }
  };

  const handleRemoveFromHighNest = async (slotIndex) => {
    const prev = [...highNest];
    const next = [...highNest];
    next[slotIndex] = null;
    setHighNest(next);
    try {
      const res = await apiFetch(`/api/high-nest/slot`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: nestUserId, slotIndex }),
      });
      if (!res.ok) throw new Error();
    } catch { setHighNest(prev); }
  };

  const handleCollect = async (owl) => {
    if (collectingOwlId) return;
    setCollectingOwlId(owl.id);
    try {
      const res = await apiFetch(`/api/nest/collect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: nestUserId, owlId: owl.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const mechanic = timers[owl.id]?.mechanic;
      if (mechanic) {
        setTimers((prev) => ({
          ...prev,
          [owl.id]: { ...prev[owl.id], secondsLeft: mechanic.cooldownSeconds, isReady: false },
        }));
      }
      if (data.coinsAwarded) {
        setCoinToast({ amount: data.coinsAwarded, owlName: owl.name });
        setCoins((prev) => (prev ?? 0) + data.coinsAwarded);
      }
    } catch { /* silent */ }
    finally { setCollectingOwlId(null); }
  };

  const handleActivateAll = async (owl) => {
    if (collectingOwlId) return;
    setCollectingOwlId(owl.id);
    try {
      const res = await apiFetch(`/api/nest/collect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: nestUserId, owlId: owl.id }),
      });
      if (!res.ok) throw new Error();
      await fetchTimers(nestUserId);
    } catch { /* silent */ }
    finally { setCollectingOwlId(null); }
  };

  const handleActivate = (owl) => setActivateSource(owl);

  const handleActivateTarget = async (targetOwl) => {
    if (!activateSource) return;
    const source = activateSource;
    setActivateSource(null);
    setCollectingOwlId(source.id);
    try {
      const res = await apiFetch(`/api/nest/collect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: nestUserId, owlId: source.id, targetOwlId: targetOwl.id }),
      });
      if (!res.ok) throw new Error();
      await fetchTimers(nestUserId);
    } catch { /* silent */ }
    finally { setCollectingOwlId(null); }
  };

  // Deduplicate userOwls for the collection grid — same owl id collapsed into one card with a count
  const deduplicatedOwls = useMemo(() => {
    const map = new Map();
    userOwls.forEach((owl) => {
      if (map.has(owl.id)) {
        map.get(owl.id).count += 1;
      } else {
        map.set(owl.id, { ...owl, count: 1 });
      }
    });
    return Array.from(map.values());
  }, [userOwls]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="on-loading">
        <div className="on-loading-card">
          <Loader className="on-spinner" />
          <p>Loading your owl collection…</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="on-loading">
        <div className="on-loading-card">
          <p className="on-error-text">{error}</p>
          <button className="on-btn-primary" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="on-page">

      {/* ── Header ── */}
      <div className="on-header">
        <div className="on-header-left">
          <span className="on-header-owl">🦉</span>
          <div>
            <h1 className="on-title">{username}'s Owl Nest</h1>
            <p className="on-subtitle">
              {userOwls.length} owl{userOwls.length !== 1 ? "s" : ""} collected
            </p>
          </div>
        </div>
        <div className="on-header-actions">
          <div className="on-coin-badge" aria-label={`${coins ?? 0} coins`}>
            <span aria-hidden="true">🪙</span>
            <span>{coins !== null ? coins.toLocaleString() : "—"}</span>
          </div>
          <button
            className="on-btn-secondary"
            onClick={() => setShowFullCollection((v) => !v)}
          >
            {showFullCollection
              ? <><List size={15} /> My Collection</>
              : <><LayoutGrid size={15} /> Full Encyclopedia</>}
          </button>
          <button className="on-btn-primary" onClick={() => navigate("/owlCollectingGacha")}>
            <Dices size={15} /> Roll Owls
          </button>
        </div>
      </div>

      <div className="on-body">

        {/* ── Collection Progress ── */}
        <section className="on-card">
          <h2 className="on-card-title">🏆 Collection Progress</h2>
          <div className="on-rarity-list">
            {rarityOrder.map((rarity) => {
              const { collected = 0, total = 0 } = rarityStats[rarity] || {};
              const pct = total > 0 ? (collected / total) * 100 : 0;
              return (
                <div key={rarity} className="on-rarity-row" style={{ borderLeftColor: rarityColors[rarity] }}>
                  <span className="on-rarity-name" style={{ color: rarityColors[rarity] }}>
                    {rarity}
                  </span>
                  <div className="on-progress-track">
                    <div
                      className="on-progress-fill"
                      style={{ width: `${pct}%`, background: rarityColors[rarity] }}
                    />
                  </div>
                  <span className="on-rarity-count">{collected}/{total}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── High Nest ── */}
        <section className="on-card on-highnest-card">
          <h2 className="on-card-title">⭐ High Nest — Favourites</h2>
          <p className="on-card-sub">
            {activateSource
              ? `Choose a target for ${activateSource.name}'s ability — press Esc to cancel`
              : selectedOwl
              ? `Click an empty slot to place ${selectedOwl.name}`
              : "Click an owl in your collection to view it and place it here"}
          </p>

          <div className="on-highnest-grid">
            {highNest.map((owl, i) => {
              const timerState = owl ? timers[owl.id] : null;
              const isTarget   = !!(activateSource && owl && owl.id !== activateSource.id);
              const isSource   = !!(activateSource && owl?.id === activateSource.id);
              const progress   = timerState?.mechanic
                ? Math.min(1, 1 - timerState.secondsLeft / timerState.mechanic.cooldownSeconds)
                : 1;

              return (
                <div
                  key={i}
                  className={[
                    "on-nest-slot",
                    owl ? "on-nest-slot--filled" : "on-nest-slot--empty",
                    isTarget ? "on-nest-slot--target" : "",
                    isSource ? "on-nest-slot--source" : "",
                  ].join(" ")}
                  style={owl ? { "--rarity-color": rarityColors[owl.rarity] } : {}}
                  onClick={() => {
                    if (activateSource && isTarget) { handleActivateTarget(owl); return; }
                    if (!activateSource && !selectedOwl && owl) { setPopupOwl(owl); return; }
                    if (!activateSource) handleHighNestSlotClick(i);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={owl ? `Nest slot ${i + 1}: ${owl.name}` : `Empty nest slot ${i + 1}`}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    if (activateSource && isTarget) { handleActivateTarget(owl); return; }
                    if (!activateSource && !selectedOwl && owl) { setPopupOwl(owl); return; }
                    if (!activateSource) handleHighNestSlotClick(i);
                  }}
                >
                  {owl ? (
                    <>
                      {/* Remove button */}
                      {!activateSource && (
                        <button
                          className="on-nest-remove"
                          onClick={(e) => { e.stopPropagation(); handleRemoveFromHighNest(i); }}
                          title="Remove from nest"
                        >
                          <X size={13} />
                        </button>
                      )}

                      {/* Owl image */}
                      <img src={getOwlImage(owl)} alt={owl.name} className="on-nest-owl-img" />

                      {/* Name */}
                      <p className="on-nest-name">{owl.name}</p>

                      {/* Rarity badge */}
                      <span className="on-rarity-badge" style={{ background: rarityColors[owl.rarity] }}>
                        {owl.rarity}
                      </span>

                      {/* Mechanic tooltip */}
                      {owl.mechanic_description && (
                        <span className="on-mechanic-tip" title={owl.mechanic_description}>
                          {owl.mechanic_description}
                        </span>
                      )}

                      {/* Timer section */}
                      {timerState && (
                        <div className="on-timer-wrap">
                          <div className="on-timer-bar">
                            <div
                              className="on-timer-fill"
                              style={{ width: `${progress * 100}%`, background: rarityColors[owl.rarity] }}
                            />
                          </div>
                          <span className={`on-timer-text ${timerState.isReady ? "on-timer-text--ready" : ""}`}>
                            {timerState.isReady ? "Ready!" : formatTime(timerState.secondsLeft)}
                          </span>
                        </div>
                      )}

                      {/* Collect / Activate button */}
                      {timerState?.isReady && !activateSource && (
                        <button
                          className={`on-collect-btn ${
                            timerState.mechanic?.type === "coins"
                              ? "on-collect-btn--coins"
                              : "on-collect-btn--ability"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (timerState.mechanic?.type === "coins") {
                              handleCollect(owl);
                            } else if (timerState.mechanic?.type === "reduce_all_percent") {
                              handleActivateAll(owl);
                            } else {
                              handleActivate(owl);
                            }
                          }}
                          disabled={collectingOwlId === owl.id}
                          aria-label={
                            timerState.mechanic?.type === "coins"
                              ? `Collect ${timerState.mechanic.coins} coins from ${owl.name}`
                              : `Activate ${owl.name}'s ability`
                          }
                        >
                          {collectingOwlId === owl.id
                            ? <span className="on-collecting-dots">…</span>
                            : timerState.mechanic?.type === "coins"
                            ? `+${timerState.mechanic.coins} 🪙`
                            : "⚡ Activate"}
                        </button>
                      )}

                      {/* Target-selection overlay */}
                      {isTarget && (
                        <div className="on-target-badge">Click to target</div>
                      )}
                      {isSource && (
                        <div className="on-source-badge">Using ability…</div>
                      )}
                    </>
                  ) : (
                    <div className="on-nest-empty">
                      <span className="on-nest-egg">🥚</span>
                      <span>Empty Nest</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Full Encyclopedia ── */}
        {showFullCollection && (
          <section className="on-card">
            <h2 className="on-card-title">📚 Complete Owl Encyclopedia</h2>
            {rarityOrder.map((rarity) => {
              const owlsOfRarity = allOwls.filter((o) => o.rarity === rarity);
              if (!owlsOfRarity.length) return null;
              return (
                <div key={rarity} className="on-rarity-section">
                  <div className="on-rarity-banner" style={{ background: rarityColors[rarity] }}>
                    {rarity} Owls — {rarityStats[rarity]?.collected || 0}/{owlsOfRarity.length}
                  </div>
                  <div className="on-owl-grid">
                    {owlsOfRarity.map((owl) => {
                      const collected  = isOwlCollected(owl.id);
                      const isSelected = selectedOwl?.id === owl.id;
                      return (
                        <div
                          key={owl.id}
                          className={`on-owl-card ${collected ? "" : "on-owl-card--locked"} ${isSelected ? "on-owl-card--selected" : ""}`}
                          style={isSelected ? { borderColor: rarityColors[owl.rarity] } : {}}
                          onClick={() => handleOwlClick(owl)}
                          role="button"
                          tabIndex={collected ? 0 : -1}
                          aria-label={collected ? `View ${owl.name}` : `${owl.name} — not yet collected`}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOwlClick(owl); } }}
                        >
                          <div className="on-owl-img-wrap">
                            <img
                              src={getOwlImage(owl)}
                              alt={owl.name}
                              className={`on-owl-img ${!collected ? "on-owl-img--locked" : ""}`}
                            />
                            {!collected && <span className="on-owl-lock">?</span>}
                          </div>
                          <p className="on-owl-name">{collected ? owl.name : "???"}</p>
                          <span className="on-rarity-badge" style={{ background: rarityColors[owl.rarity] }}>
                            {owl.rarity}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* ── Your Collected Owls ── */}
        {!showFullCollection && (
          <section
            className="on-card on-collection-card"
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/owlNestImg.png)` }}
          >
            <h2 className="on-card-title">🦉 Your Collected Owls</h2>
            {userOwls.length === 0 ? (
              <div className="on-empty">
                <p>No owls collected yet!</p>
                <p className="on-empty-sub">Roll the gacha to start building your collection.</p>
                <button
                  className="on-btn-primary"
                  style={{ marginTop: "1rem" }}
                  onClick={() => navigate("/owlCollectingGacha")}
                >
                  <Dices size={15} /> Roll Now
                </button>
              </div>
            ) : (
              <div className="on-owl-grid">
                {deduplicatedOwls.map((owl) => (
                  <div
                    key={`user-${owl.id}`}
                    className="on-owl-card"
                    onClick={() => handleOwlClick(owl)}
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${owl.name}${owl.count > 1 ? ` (×${owl.count})` : ""}`}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOwlClick(owl); } }}
                  >
                    {owl.count > 1 && (
                      <span className="on-owl-count">×{owl.count}</span>
                    )}
                    <div className="on-owl-img-wrap">
                      <img src={getOwlImage(owl)} alt={owl.name} className="on-owl-img" />
                    </div>
                    <p className="on-owl-name">{owl.name}</p>
                    <span className="on-rarity-badge" style={{ background: rarityColors[owl.rarity] }}>
                      {owl.rarity}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

      </div>

      {/* ── Owl detail popup ── */}
      {popupOwl && (
        <div
          className="on-popup-backdrop"
          onClick={() => setPopupOwl(null)}
          role="presentation"
        >
          <div
            className={`on-popup on-popup--${popupOwl.rarity.toLowerCase()}`}
            ref={popupRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="owl-popup-name"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="on-popup-close"
              onClick={() => setPopupOwl(null)}
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="on-popup-glow" aria-hidden="true" />

            <div className="on-popup-img-wrap">
              <img src={getOwlImage(popupOwl)} alt={popupOwl.name} className="on-popup-img" />
            </div>

            <h2 id="owl-popup-name" className="on-popup-name">{popupOwl.name}</h2>
            <span
              className="on-rarity-badge on-popup-rarity"
              style={{ background: rarityColors[popupOwl.rarity] }}
            >
              {popupOwl.rarity}
            </span>

            <p className="on-popup-desc">{popupOwl.description}</p>

            {popupOwl.mechanic_description && (
              <div className="on-popup-mechanic">
                <span className="on-popup-mechanic-label">⚙️ Ability</span>
                <p className="on-popup-mechanic-text">{popupOwl.mechanic_description}</p>
              </div>
            )}

            <div className="on-popup-actions">
              {!isInHighNest(popupOwl.id) ? (
                <button
                  className="on-btn-primary"
                  onClick={() => { setSelectedOwl(popupOwl); setPopupOwl(null); }}
                >
                  Place in Nest
                </button>
              ) : (
                <span className="on-popup-in-nest">✓ In High Nest</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Selection toast ── */}
      {selectedOwl && (
        <div className="on-selection-toast">
          <span>Selected: <strong>{selectedOwl.name}</strong> — click an empty nest slot</span>
          <button className="on-deselect" onClick={() => setSelectedOwl(null)}>
            <X size={14} /> Deselect
          </button>
        </div>
      )}

      {/* ── Coin reward toast ── */}
      {coinToast && (
        <div className="on-coin-toast" aria-live="polite">
          +{coinToast.amount} 🪙
        </div>
      )}
    </div>
  );
};

export default OwlNestPage;
